import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/business.scss";
import { useI18n } from "../../i18n/I18nProvider";


function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

interface StudentProject {
    title: string;
    deadline: string;
    description: string;
}


const CONTACT_EMAIL = "cc@wsiz.edu.pl";


const BusinessPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const suggestedTopics = [
        t("business_topic_1"), t("business_topic_2"), t("business_topic_3"),
        t("business_topic_4"), t("business_topic_5"), t("business_topic_6"),
    ];
    const benefits = [
        t("business_benefit_1"), t("business_benefit_2"),
        t("business_benefit_3"), t("business_benefit_4"),
    ];
    const { ref: refIntro, inView: inViewIntro } = useInView(0.15);
    const { ref: refList, inView: inViewList } = useInView(0.1);
    const { ref: refSubmit, inView: inViewSubmit } = useInView(0.15);
    const { ref: refBenefits, inView: inViewBenefits } = useInView(0.15);
    const { ref: refCta, inView: inViewCta } = useInView(0.15);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("business-page", { "business-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("business_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />


            <MatrixHero
                title={t("business_hero_title")}
                subtitle={t("business_hero_subtitle")}
                density={0.5}
            />

            <div className="business-page__inner">


                <article
                    ref={refIntro}
                    className={classNames("biz-card", { "biz-card--visible": inViewIntro })}
                >
                    <div className="biz-card__glow" aria-hidden="true" />
                    <h2 className="biz-card__title">{t("business_intro_title")}</h2>
                    <p className="biz-card__text">
                        <TypewriterText text={t("business_intro_text")} active={inViewIntro} speed={4} />
                    </p>
                </article>
                <div
                    ref={refCta}
                    className={classNames("biz-cta", { "biz-cta--visible": inViewCta })}
                >
                    <h2 className="biz-cta__heading">{t("business_cta_heading")}</h2>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="biz-cta__email">

                        {CONTACT_EMAIL}
                    </a>
                </div>

                {/* <div
                    ref={refList}
                    className={classNames("biz-list-section", { "biz-list-section--visible": inViewList })}
                >
                    <h2 className="biz-list-section__title">Projekty studenckie</h2>

                    <ul className="biz-list">
                        {STUDENT_PROJECTS.map((project, idx) => (
                            <li
                                key={idx}
                                className="biz-item"
                                style={{ animationDelay: `${idx * 0.1}s` }}
                            >
                                <div className="biz-item__header">
                                    <h3 className="biz-item__title">{project.title}</h3>
                                    <span className="biz-item__deadline">
                                        <span className="material-symbols-outlined biz-item__deadline-icon" aria-hidden="true">
                                            schedule
                                        </span>
                                        {project.deadline}
                                    </span>
                                </div>
                                <p className="biz-item__desc">{project.description}</p>
                            </li>
                        ))}
                    </ul>
                </div> */}


                <article
                    ref={refSubmit}
                    className={classNames("biz-card biz-card--accent", { "biz-card--visible": inViewSubmit })}
                >
                    <div className="biz-card__glow" aria-hidden="true" />
                    <h2 className="biz-card__title">{t("business_submit_title")}</h2>
                    <p className="biz-card__text">
                        {t("business_submit_text")}
                    </p>
                    <p className="biz-card__text">{t("business_submit_lead")}</p>
                    <ul className="biz-topics">
                        {suggestedTopics.map((topic, i) => (
                            <li key={i} className="biz-topics__item">{topic}</li>
                        ))}
                    </ul>
                    <p className="biz-card__text">
                        {t("business_submit_footer")}
                    </p>
                </article>


                <div
                    ref={refBenefits}
                    className={classNames("biz-benefits", { "biz-benefits--visible": inViewBenefits })}
                >
                    <h2 className="biz-benefits__title">{t("business_benefits_title")}</h2>
                    <div className="biz-benefits__grid">
                        {benefits.map((benefit, i) => (
                            <div key={i} className="benefit-tile" style={{ animationDelay: `${i * 0.1}s` }}>
                                <span className="benefit-tile__text">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
};

export default BusinessPage;
